import { Button, ButtonProps } from '@commenda/kakashi';
import { cn } from '@commenda/utils';

interface PageEmptyStateAction {
  label: string;
  onClick?: () => void;
  /** Leading icon, e.g. <Icons.Plus className="size-4" />. */
  icon?: React.ReactNode;
  /** Defaults: primary for the first action, secondary for the second. */
  variant?: ButtonProps['variant'];
}

interface PageEmptyStateProps {
  title: string;
  description?: React.ReactNode;
  /** Optional illustration/icon rendered above the title. */
  icon?: React.ReactNode;
  primaryAction?: PageEmptyStateAction;
  secondaryAction?: PageEmptyStateAction;
  /** Extra classes for the centering wrapper (e.g. vertical padding). */
  className?: string;
}

/**
 * Standard page-level empty state: a centered block with a title, supporting
 * copy, and up to two actions. Use for full-page "nothing here yet" screens
 * (e.g. Transactions, Registrations). For card/section empty states with a
 * background, use EmptyState / EmptyStateV2 instead.
 */
function PageEmptyState({
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  className,
}: PageEmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center flex-1', className)}
    >
      <div className="flex flex-col gap-xl">
        {icon}
        <div className="flex flex-col gap-md">
          <h3 className="txt-large-bold text-text-primary">{title}</h3>
          {description ? (
            <p className="txt-small-regular text-text-secondary">
              {description}
            </p>
          ) : null}
        </div>
        {primaryAction || secondaryAction ? (
          <div className="flex items-center gap-md">
            {primaryAction ? (
              <Button
                variant={primaryAction.variant ?? 'primary'}
                size="md"
                onClick={primaryAction.onClick}
              >
                {primaryAction.icon}
                <span>{primaryAction.label}</span>
              </Button>
            ) : null}
            {secondaryAction ? (
              <Button
                variant={secondaryAction.variant ?? 'secondary'}
                size="md"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.icon}
                <span>{secondaryAction.label}</span>
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { PageEmptyState };
